import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { format, addDays, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, isBefore, parseISO } from "date-fns";

// UI Components
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Toaster, toast } from "sonner";
import { 
  Phone, Mail, MapPin, Calendar, Clock, CheckCircle2, ChevronLeft, ChevronRight,
  Shield, Users, FileText, DollarSign, Menu, X, ArrowRight, Star, LogOut,
  Download, Eye, AlertCircle, CreditCard, Building2, Briefcase
} from "lucide-react";
import AdminAvailability from "./components/AdminAvailability";
const BACKEND_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";
const API = `${BACKEND_URL}/api`;

// Business Info
const BUSINESS = {
  name: "JAJE Business Services LLC",
  email: "support@jaje.org",
  phone: "317 529 4863",
  phoneFormatted: "(317) 529-4863",
  timezone: "America/Indiana/Indianapolis"
};

// ============== SHARED COMPONENTS ==============

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { href: "/", label: "Home" },
    { href: "/pricing", label: "Services & Pricing" },
    { href: "/book", label: "Book Appointment" },
    { href: "/reserve", label: "Reserve Spot" },
    { href: "/contact", label: "Contact" },
  ];

  return (
    <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-sm border-b border-gray-100 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center space-x-2">
            <Building2 className="h-8 w-8 text-blue-600" />
            <span className="font-bold text-xl text-gray-900">JAJE</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`text-sm font-medium transition-colors ${
                  location.pathname === link.href
                    ? "text-blue-600"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link to="/admin/login">
              <Button variant="outline" size="sm">Admin</Button>
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden py-4 border-t">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className="block py-2 text-gray-600 hover:text-gray-900"
                onClick={() => setIsOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            <Link to="/admin/login" onClick={() => setIsOpen(false)}>
              <Button variant="outline" size="sm" className="mt-2">Admin</Button>
            </Link>
          </div>
        )}
      </div>
    </nav>
  );
};

const Footer = () => (
  <footer className="bg-gray-900 text-gray-300">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
        <div className="col-span-1 md:col-span-2">
          <div className="flex items-center space-x-2 mb-4">
            <Building2 className="h-8 w-8 text-blue-500" />
            <span className="font-bold text-xl text-white">{BUSINESS.name}</span>
          </div>
          <p className="text-gray-400 mb-4 max-w-md">
            Professional tax preparation services you can trust. We help individuals and small businesses navigate tax season with confidence.
          </p>
          <div className="flex space-x-4">
            <Badge variant="secondary" className="bg-blue-600/20 text-blue-400">Licensed</Badge>
            <Badge variant="secondary" className="bg-green-600/20 text-green-400">Insured</Badge>
          </div>
        </div>

        <div>
          <h4 className="font-semibold text-white mb-4">Quick Links</h4>
          <ul className="space-y-2">
            <li><Link to="/pricing" className="hover:text-white transition-colors">Services & Pricing</Link></li>
            <li><Link to="/book" className="hover:text-white transition-colors">Book Appointment</Link></li>
            <li><Link to="/reserve" className="hover:text-white transition-colors">Reserve Your Spot</Link></li>
            <li><Link to="/contact" className="hover:text-white transition-colors">Contact Us</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="font-semibold text-white mb-4">Contact</h4>
          <ul className="space-y-3">
            <li className="flex items-center space-x-2">
              <Mail className="h-4 w-4 text-blue-500" />
              <a href={`mailto:${BUSINESS.email}`} className="hover:text-white">{BUSINESS.email}</a>
            </li>
            <li className="flex items-center space-x-2">
              <Phone className="h-4 w-4 text-blue-500" />
              <a href={`tel:${BUSINESS.phone.replace(/\s/g, '')}`} className="hover:text-white">{BUSINESS.phoneFormatted}</a>
            </li>
          </ul>
        </div>
      </div>

      <Separator className="my-8 bg-gray-800" />

      <div className="flex flex-col md:flex-row justify-between items-center text-sm text-gray-500">
        <p>© {new Date().getFullYear()} {BUSINESS.name}. All rights reserved.</p>
        <div className="flex space-x-6 mt-4 md:mt-0">
          <Link to="/privacy" className="hover:text-gray-300">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-gray-300">Terms of Service</Link>
        </div>
      </div>
    </div>
  </footer>
);

const Layout = ({ children }) => (
  <div className="min-h-screen flex flex-col">
    <Navbar />
    <main className="flex-1 pt-16">{children}</main>
    <Footer />
    <Toaster position="top-right" richColors />
  </div>
);

// ============== HOME PAGE ==============

const HomePage = () => {
  const taxSeasonYear = new Date().getFullYear();
  const navigate = useNavigate();

  const features = [
    { icon: Shield, title: "Secure & Confidential", desc: "Your financial data is protected with industry-leading security practices." },
    { icon: Users, title: "Expert Preparers", desc: "Our team has years of experience handling complex tax situations." },
    { icon: Clock, title: "Fast Turnaround", desc: "Get your taxes done quickly without sacrificing accuracy." },
    { icon: DollarSign, title: "Maximize Refunds", desc: "We find every deduction you're entitled to." },
  ];

  const steps = [
    { num: 1, title: "Choose Your Package", desc: "Select the tax preparation service that fits your needs." },
    { num: 2, title: "Book Your Time", desc: "Pick a convenient appointment slot from our calendar." },
    { num: 3, title: "Reserve & Pay", desc: "Secure your spot with a deposit or full payment." },
    { num: 4, title: "Get Filed", desc: "We prepare and file your taxes accurately." },
  ];

  const testimonials = [
    { name: "Sarah M.", text: "JAJE made filing my taxes so easy. Highly recommend!", rating: 5 },
    { name: "James T.", text: "Professional service at a great price. Will use again next year.", rating: 5 },
    { name: "Michelle R.", text: "They found deductions I didn't even know about!", rating: 5 },
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 text-white overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yLjIxLTEuNzktNC00LTRzLTQgMS43OS00IDQgMS43OSA0IDQgNCA0LTEuNzkgNC00eiIvPjwvZz48L2c+PC9zdmc+')] opacity-20" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 relative">
          <div className="max-w-3xl">
            <Badge className="mb-4 bg-white/20 text-white border-0">
  Tax Season {taxSeasonYear}
</Badge>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              Professional Tax Preparation Made Simple
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-8 leading-relaxed">
              Expert tax services for individuals and small businesses. Book your appointment today and maximize your refund.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg" 
                className="bg-white text-blue-600 hover:bg-gray-100 font-semibold"
                onClick={() => navigate('/book')}
              >
                <Calendar className="mr-2 h-5 w-5" />
                Book Appointment
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white text-white hover:bg-white/10"
                onClick={() => navigate('/reserve')}
              >
                <CreditCard className="mr-2 h-5 w-5" />
                Reserve Your Spot
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="bg-gray-50 py-8 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center items-center gap-8 text-gray-600">
            <div className="flex items-center space-x-2">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              <span>Licensed Tax Preparers</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              <span>500+ Happy Clients</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              <span>Fast E-Filing</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              <span>Secure Processing</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose JAJE Business Services?
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              We combine expertise, technology, and personalized service to deliver outstanding results.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, i) => (
              <Card key={i} className="text-center border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="pt-8 pb-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 text-blue-600 mb-6">
                    <feature.icon className="h-8 w-8" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                  <p className="text-gray-600">{feature.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-xl text-gray-600">
              Four simple steps to stress-free tax filing
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, i) => (
              <div key={i} className="relative">
                <div className="flex flex-col items-center text-center">
                  <div className="w-12 h-12 rounded-full bg-blue-600 text-white flex items-center justify-center text-xl font-bold mb-4">
                    {step.num}
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{step.title}</h3>
                  <p className="text-gray-600">{step.desc}</p>
                </div>
                {i < steps.length - 1 && (
                  <ArrowRight className="hidden lg:block absolute top-6 -right-4 h-6 w-6 text-gray-300" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Our Clients Say
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((t, i) => (
              <Card key={i} className="border-0 shadow-lg">
                <CardContent className="pt-6">
                  <div className="flex mb-4">
                    {[...Array(t.rating)].map((_, j) => (
                      <Star key={j} className="h-5 w-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <p className="text-gray-600 mb-4 italic">"{t.text}"</p>
                  <p className="font-semibold">{t.name}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto text-center px-4">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Book your appointment today and let us handle your taxes with care and expertise.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-white text-blue-600 hover:bg-gray-100"
              onClick={() => navigate('/book')}
            >
              Book Appointment
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-white text-white hover:bg-white/10"
              onClick={() => navigate('/pricing')}
            >
              View Pricing
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

// ============== PRICING PAGE ==============

const PricingPage = () => {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/packages`)
      .then(res => setPackages(res.data))
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  const getPackageIcon = (name) => {
    if (name.includes("Single")) return FileText;
    if (name.includes("Married")) return Users;
    if (name.includes("Head")) return Users;
    if (name.includes("Business")) return Briefcase;
    return FileText;
  };

  const faqs = [
    {
      q: "What documents do I need to bring?",
      a: "Bring your W-2s, 1099s, Social Security numbers for you and dependents, last year's tax return, and any receipts for deductions."
    },
    {
      q: "How long does tax preparation take?",
      a: "Most returns are completed within 1-2 hours. Complex business returns may require additional time."
    },
    {
      q: "What counts as a small business?",
      a: "Small business filing includes sole proprietors, freelancers, contractors, and businesses with Schedule C income."
    },
    {
      q: "Can my price change after I book?",
      a: "Final price may change if additional forms are required (multiple W-2s, investments, rental income, etc.). You will always be informed before we file."
    },
    {
      q: "How do I get my refund?",
      a: "Refunds can be direct deposited to your bank account, typically within 7-21 days after IRS acceptance."
    }
  ];

  return (
    <Layout>
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4" variant="secondary">Transparent Pricing</Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Tax Preparation Packages
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Choose the package that fits your tax situation. All prices include e-filing.
            </p>
          </div>

          {loading ? (
            <div className="text-center py-12">Loading packages...</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
              {packages.map((pkg, i) => {
                const Icon = getPackageIcon(pkg.name);
                const isPopular = pkg.name.includes("Married Filing Jointly");
                return (
                  <Card key={pkg.id} className={`relative ${isPopular ? 'border-blue-500 border-2 shadow-xl' : 'border shadow-lg'}`}>
                    {isPopular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <Badge className="bg-blue-500">Most Popular</Badge>
                      </div>
                    )}
                    <CardHeader className="text-center pb-2">
                      <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-blue-100 text-blue-600 mx-auto mb-4">
                        <Icon className="h-7 w-7" />
                      </div>
                      <CardTitle className="text-xl">{pkg.name}</CardTitle>
                      <CardDescription>{pkg.description}</CardDescription>
                    </CardHeader>
                    <CardContent className="text-center">
                      <div className="mb-6">
                        <span className="text-4xl font-bold">${pkg.price}</span>
                      </div>
                      <ul className="text-left space-y-3 mb-6">
                        <li className="flex items-center">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                          <span>Professional Preparation</span>
                        </li>
                        <li className="flex items-center">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                          <span>E-Filing Included</span>
                        </li>
                        <li className="flex items-center">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                          <span>Audit Support</span>
                        </li>
                        <li className="flex items-center">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                          <span>Copy for Your Records</span>
                        </li>
                      </ul>
                    </CardContent>
                    <CardFooter className="flex flex-col gap-2">
                      <Button 
                        className="w-full" 
                        onClick={() => navigate(`/book?package=${pkg.id}`)}
                      >
                        Book Appointment
                      </Button>
                      <Button 
                        variant="outline" 
                        className="w-full"
                        onClick={() => navigate(`/reserve?package=${pkg.id}`)}
                      >
                        Reserve & Pay
                      </Button>
                    </CardFooter>
                  </Card>
                );
              })}
            </div>
          )}

          <Alert className="max-w-3xl mx-auto mb-16 bg-amber-50 border-amber-200">
            <AlertCircle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800">
              <strong>Note:</strong> Final price may change if additional forms are required (multiple W-2s, investments, rental income, etc.). You will always be informed before we file.
            </AlertDescription>
          </Alert>
          {/* =================== BY FILING STATUS PRICING =================== */}
          <div className="max-w-5xl mx-auto mb-16">
            <Card className="border shadow-lg">
              <CardHeader className="text-center">
                <Badge className="mx-auto mb-3" variant="secondary">Minimum fee: $80</Badge>
                <CardTitle className="text-2xl md:text-3xl">
                  JAJE Business Services LLC — Tax Preparation Prices (By Filing Status)
                </CardTitle>
                <CardDescription>
                  Federal return, e-filing, and standard credits included where applicable.
                </CardDescription>
              </CardHeader>

              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Single */}
                  <Card className="border">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <FileText className="h-5 w-5 text-blue-600" />
                        Single
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <ul className="space-y-2 text-gray-700">
                        <li className="flex justify-between gap-4">
                          <span>W-2 only</span>
                          <span className="font-semibold">$80</span>
                        </li>
                        <li className="flex justify-between gap-4">
                          <span>W-2 + 1 dependent</span>
                          <span className="font-semibold">$100</span>
                        </li>
                        <li className="flex justify-between gap-4">
                          <span>W-2 + 2+ dependents</span>
                          <span className="font-semibold">$120</span>
                        </li>
                      </ul>
                    </CardContent>
                  </Card>

                  {/* MFJ */}
                  <Card className="border">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Users className="h-5 w-5 text-blue-600" />
                        Married Filing Jointly (MFJ)
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <ul className="space-y-2 text-gray-700">
                        <li className="flex justify-between gap-4">
                          <span>W-2 only</span>
                          <span className="font-semibold">$120</span>
                        </li>
                        <li className="flex justify-between gap-4">
                          <span>W-2 + 1 dependent</span>
                          <span className="font-semibold">$150</span>
                        </li>
                        <li className="flex justify-between gap-4">
                          <span>W-2 + 2+ dependents</span>
                          <span className="font-semibold">$200</span>
                        </li>
                      </ul>
                    </CardContent>
                  </Card>

                  {/* MFS */}
                  <Card className="border">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Users className="h-5 w-5 text-blue-600" />
                        Married Filing Separately (MFS)
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <ul className="space-y-2 text-gray-700">
                        <li className="flex justify-between gap-4">
                          <span>W-2 only</span>
                          <span className="font-semibold">$100</span>
                        </li>
                        <li className="flex justify-between gap-4">
                          <span>With dependents</span>
                          <span className="font-semibold">$120</span>
                        </li>
                      </ul>
                      <p className="text-sm text-gray-500 mt-3">
                        MFS requires extra checks—price reflects complexity.
                      </p>
                    </CardContent>
                  </Card>

                  {/* HOH */}
                  <Card className="border">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Users className="h-5 w-5 text-blue-600" />
                        Head of Household (HOH)
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <ul className="space-y-2 text-gray-700">
                        <li className="flex justify-between gap-4">
                          <span>W-2 only</span>
                          <span className="font-semibold">$110</span>
                        </li>
                        <li className="flex justify-between gap-4">
                          <span>With dependents</span>
                          <span className="font-semibold">$130</span>
                        </li>
                      </ul>
                    </CardContent>
                  </Card>
                </div>

                <Separator className="my-8" />

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Add-ons */}
                  <div>
                    <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                      <DollarSign className="h-5 w-5 text-blue-600" />
                      Situations That Increase the Price (Add-Ons)
                    </h3>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex justify-between gap-4">
                        <span>Self-employed (Schedule C)</span>
                        <span className="font-semibold">Starts at $180</span>
                      </li>
                      <li className="text-sm text-gray-500 -mt-1">
                        +$25 per additional 1099
                      </li>
                      <li className="flex justify-between gap-4 pt-2">
                        <span>Rental income (Schedule E)</span>
                        <span className="font-semibold">Starts at $200</span>
                      </li>
                      <li className="flex justify-between gap-4">
                        <span>Additional state return</span>
                        <span className="font-semibold">+$60 / state</span>
                      </li>
                      <li className="flex justify-between gap-4">
                        <span>Amended return (1040-X)</span>
                        <span className="font-semibold">$150</span>
                      </li>
                      <li className="flex justify-between gap-4">
                        <span>Prior-year return (each year)</span>
                        <span className="font-semibold">$90</span>
                      </li>
                    </ul>
                  </div>

                  {/* Includes */}
                  <div>
                    <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                      What the Base Price Includes
                    </h3>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-center">
                        <CheckCircle2 className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                        <span>Federal return</span>
                      </li>
                      <li className="flex items-center">
                        <CheckCircle2 className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                        <span>Electronic filing</span>
                      </li>
                      <li className="flex items-center">
                        <CheckCircle2 className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                        <span>PTIN-signed return</span>
                      </li>
                      <li className="flex items-center">
                        <CheckCircle2 className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                        <span>Copy of filed return</span>
                      </li>
                      <li className="flex items-center">
                        <CheckCircle2 className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                        <span>Standard credits (CTC/EITC if applicable)</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          {/* ================= END BY FILING STATUS PRICING ================== */}

          {/* FAQs */}
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-8">Frequently Asked Questions</h2>
            <div className="space-y-4">
              {faqs.map((faq, i) => (
                <Card key={i}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">{faq.q}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">{faq.a}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

// ============== BOOK APPOINTMENT PAGE ==============

const BookAppointmentPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(searchParams.get('package') || '');
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    notes: ''
  });

  useEffect(() => {
    axios.get(`${API}/packages`)
      .then(res => setPackages(res.data))
      .catch(err => console.error(err));
  }, []);

  useEffect(() => {
    if (selectedDate) {
      setLoading(true);
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      axios.get(`${API}/availability?date=${dateStr}`)
        .then(res => {
          setAvailability(res.data);
          setSelectedSlot(null);
        })
        .catch(err => console.error(err))
        .finally(() => setLoading(false));
    }
  }, [selectedDate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedPackage || !selectedDate || !selectedSlot) {
      toast.error('Please select a package, date, and time slot');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/appointments/create`, {
        customer_name: formData.name,
        customer_email: formData.email,
        customer_phone: formData.phone,
        package_id: selectedPackage,
        start_time: selectedSlot.start_iso,
        notes: formData.notes
      });

      setBookingResult(response.data.appointment);
      setShowConfirmation(true);
      toast.success('Appointment booked successfully!');
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to book appointment';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const daysInMonth = eachDayOfInterval({
    start: startOfMonth(currentMonth),
    end: endOfMonth(currentMonth)
  });

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const firstDayOfMonth = startOfMonth(currentMonth).getDay();

  return (
    <Layout>
      <section className="py-20 bg-gray-50 min-h-screen">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Book Your Appointment</h1>
            <p className="text-xl text-gray-600">Choose your package and select a convenient time slot</p>
          </div>

          {showConfirmation && bookingResult ? (
            <Card className="max-w-2xl mx-auto">
              <CardContent className="pt-8 text-center">
                <div className="w-20 h-20 rounded-full bg-green-100 text-green-600 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="h-10 w-10" />
                </div>
                <h2 className="text-2xl font-bold mb-4">Appointment Confirmed!</h2>
                <p className="text-gray-600 mb-6">
                  Your appointment has been booked successfully. A confirmation email will be sent to {bookingResult.customer_email}.
                </p>
                <div className="bg-gray-50 rounded-lg p-6 text-left mb-6">
                  <h3 className="font-semibold mb-4">Appointment Details:</h3>
                  <div className="space-y-2 text-sm">
                    <p><strong>Service:</strong> {bookingResult.package_name}</p>
                    <p><strong>Date:</strong> {format(parseISO(bookingResult.start_time), 'MMMM d, yyyy')}</p>
                    <p><strong>Time:</strong> {format(parseISO(bookingResult.start_time), 'h:mm a')}</p>
                    <p><strong>Price:</strong> ${bookingResult.package_price}</p>
                    <p><strong>Confirmation #:</strong> {bookingResult.id.slice(0, 8).toUpperCase()}</p>
                  </div>
                </div>
                <Alert className="text-left mb-6">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Please bring your W-2s, ID, Social Security cards, and last year's tax return to your appointment.
                  </AlertDescription>
                </Alert>
                <div className="flex gap-4 justify-center">
                  <Button onClick={() => navigate('/')}>Return Home</Button>
                  <Button variant="outline" onClick={() => {
                    setShowConfirmation(false);
                    setBookingResult(null);
                    setSelectedPackage('');
                    setSelectedDate(null);
                    setSelectedSlot(null);
                    setFormData({ name: '', email: '', phone: '', notes: '' });
                  }}>Book Another</Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Calendar */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>Select Date & Time</span>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="icon"
                        onClick={() => setCurrentMonth(d => addDays(startOfMonth(d), -1))}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="icon"
                        onClick={() => setCurrentMonth(d => addDays(endOfMonth(d), 1))}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardTitle>
                  <CardDescription>
                    {format(currentMonth, 'MMMM yyyy')} • Eastern Time (Indianapolis)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-7 gap-1 mb-4">
                    {dayNames.map(day => (
                      <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                        {day}
                      </div>
                    ))}
                    {[...Array(firstDayOfMonth)].map((_, i) => (
                      <div key={`empty-${i}`} />
                    ))}
                    {daysInMonth.map(day => {
                      const isPast = isBefore(day, new Date()) && !isToday(day);
                      const isSunday = day.getDay() === 0;
                      const isSelected = selectedDate && format(selectedDate, 'yyyy-MM-dd') === format(day, 'yyyy-MM-dd');
                      const isDisabled = isPast;

                      return (
                        <button
                          key={day.toISOString()}
                          onClick={() => !isDisabled && setSelectedDate(day)}
                          disabled={isDisabled}
                          className={`
                            p-3 rounded-lg text-center transition-all
                            ${isSelected ? 'bg-blue-600 text-white' : ''}
                            ${isToday(day) && !isSelected ? 'bg-blue-100 text-blue-700 font-semibold' : ''}
                            ${isDisabled ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-100'}
                            ${!isSameMonth(day, currentMonth) ? 'text-gray-300' : ''}
                          `}
                        >
                          {format(day, 'd')}
                        </button>
                      );
                    })}
                  </div>

                  {selectedDate && (
                    <div className="mt-6">
                      <h4 className="font-semibold mb-4">
                        Available Times for {format(selectedDate, 'MMMM d, yyyy')}
                      </h4>
                      {loading ? (
                        <p className="text-gray-500">Loading available slots...</p>
                      ) : availability?.slots?.length > 0 ? (
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                          {availability.slots.filter(s => s.available !== false).map((slot, i) => (
                            <Button
                              key={i}
                              variant={selectedSlot?.start === slot.start ? "default" : "outline"}
                              size="sm"
                              onClick={() => setSelectedSlot(slot)}
                            >
                              {slot.start}
                            </Button>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500">{availability?.message || 'No available slots for this date'}</p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Booking Form */}
              <Card>
                <CardHeader>
                  <CardTitle>Your Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <Label htmlFor="package">Service Package</Label>
                      <Select value={selectedPackage} onValueChange={setSelectedPackage}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a package" />
                        </SelectTrigger>
                        <SelectContent>
                          {packages.map(pkg => (
                            <SelectItem key={pkg.id} value={pkg.id}>
                              {pkg.name} - ${pkg.price}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="name">Full Name</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={e => setFormData({...formData, name: e.target.value})}
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={e => setFormData({...formData, email: e.target.value})}
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="phone">Phone</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={e => setFormData({...formData, phone: e.target.value})}
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="notes">Notes (Optional)</Label>
                      <Textarea
                        id="notes"
                        value={formData.notes}
                        onChange={e => setFormData({...formData, notes: e.target.value})}
                        placeholder="Any special requests or questions?"
                      />
                    </div>

                    {selectedSlot && (
                      <Alert className="bg-blue-50 border-blue-200">
                        <Clock className="h-4 w-4 text-blue-600" />
                        <AlertDescription className="text-blue-800">
                          Selected: {format(selectedDate, 'MMM d')} at {selectedSlot.start}
                        </AlertDescription>
                      </Alert>
                    )}

                    <Button 
                      type="submit" 
                      className="w-full" 
                      disabled={submitting || !selectedPackage || !selectedSlot}
                    >
                      {submitting ? 'Booking...' : 'Book Appointment'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

// ============== RESERVE SPOT PAGE ==============

const ReserveSpotPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(searchParams.get('package') || '');
  const [paymentType, setPaymentType] = useState('full');
  const [submitting, setSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [reservationResult, setReservationResult] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });

  useEffect(() => {
    axios.get(`${API}/packages`)
      .then(res => setPackages(res.data))
      .catch(err => console.error(err));
  }, []);

  const selectedPkg = packages.find(p => p.id === selectedPackage);
  const amount = selectedPkg ? (paymentType === 'deposit' ? selectedPkg.price * 0.5 : selectedPkg.price) : 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedPackage) {
      toast.error('Please select a package');
      return;
    }

    setSubmitting(true);
    try {
      // Create reservation
      const response = await axios.post(`${API}/reservations/create`, {
        customer_name: formData.name,
        customer_email: formData.email,
        customer_phone: formData.phone,
        package_id: selectedPackage,
        payment_type: paymentType
      });

      setReservationResult(response.data.reservation);
      setShowConfirmation(true);
      toast.success('Reservation created!');
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to create reservation';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePayment = async () => {
    if (!reservationResult) return;
    
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/reservations/create-checkout-session?reservation_id=${reservationResult.id}`);
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        toast.error('Unable to start payment. Please contact support.');
      }
    } catch (err) {
      const message = err.response?.data?.detail || 'Payment system not available';
      toast.error(message);
      // Mark as paid for demo if Stripe is not configured
      if (message.includes('not configured')) {
        toast.info('Demo mode: Marking as paid for testing');
        setReservationResult({ ...reservationResult, payment_status: 'paid' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <section className="py-20 bg-gray-50 min-h-screen">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Reserve Your Tax Prep Spot</h1>
            <p className="text-xl text-gray-600">Secure your spot now with a deposit or full payment</p>
          </div>

          {showConfirmation && reservationResult ? (
            <Card className="max-w-2xl mx-auto">
              <CardContent className="pt-8 text-center">
                {reservationResult.payment_status === 'paid' ? (
                  <>
                    <div className="w-20 h-20 rounded-full bg-green-100 text-green-600 flex items-center justify-center mx-auto mb-6">
                      <CheckCircle2 className="h-10 w-10" />
                    </div>
                    <h2 className="text-2xl font-bold mb-4">Payment Successful!</h2>
                    <p className="text-gray-600 mb-6">
                      Your spot has been reserved. We'll contact you to schedule your appointment.
                    </p>
                  </>
                ) : (
                  <>
                    <div className="w-20 h-20 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mx-auto mb-6">
                      <CreditCard className="h-10 w-10" />
                    </div>
                    <h2 className="text-2xl font-bold mb-4">Reservation Created</h2>
                    <p className="text-gray-600 mb-6">
                      Complete your payment to secure your spot.
                    </p>
                  </>
                )}
                
                <div className="bg-gray-50 rounded-lg p-6 text-left mb-6">
                  <h3 className="font-semibold mb-4">Reservation Details:</h3>
                  <div className="space-y-2 text-sm">
                    <p><strong>Service:</strong> {reservationResult.package_name}</p>
                    <p><strong>Total Price:</strong> ${reservationResult.amount}</p>
                    <p><strong>Amount Due:</strong> ${reservationResult.deposit_amount}</p>
                    <p><strong>Payment Type:</strong> {reservationResult.payment_type === 'deposit' ? '50% Deposit' : 'Full Payment'}</p>
                    <p><strong>Status:</strong> 
                      <Badge className={`ml-2 ${reservationResult.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {reservationResult.payment_status === 'paid' ? 'Paid' : 'Pending Payment'}
                      </Badge>
                    </p>
                    <p><strong>Confirmation #:</strong> {reservationResult.id.slice(0, 8).toUpperCase()}</p>
                  </div>
                </div>

                {reservationResult.payment_status !== 'paid' && (
                  <Button onClick={handlePayment} className="w-full mb-4" disabled={submitting}>
                    {submitting ? 'Processing...' : `Pay $${reservationResult.deposit_amount} Now`}
                  </Button>
                )}

                <div className="flex gap-4 justify-center">
                  <Button variant="outline" onClick={() => navigate('/')}>Return Home</Button>
                  {reservationResult.payment_status === 'paid' && (
                    <Button onClick={() => navigate('/book')}>Book Appointment</Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Package Selection */}
              <div className="space-y-4">
                <h3 className="text-xl font-semibold mb-4">Select Your Package</h3>
                {packages.map(pkg => (
                  <Card 
                    key={pkg.id}
                    className={`cursor-pointer transition-all ${selectedPackage === pkg.id ? 'border-blue-500 border-2 shadow-lg' : 'hover:shadow-md'}`}
                    onClick={() => setSelectedPackage(pkg.id)}
                  >
                    <CardContent className="p-4 flex justify-between items-center">
                      <div>
                        <h4 className="font-semibold">{pkg.name}</h4>
                        <p className="text-sm text-gray-500">{pkg.description}</p>
                      </div>
                      <div className="text-right">
                        <span className="text-2xl font-bold">${pkg.price}</span>
                        {selectedPackage === pkg.id && (
                          <CheckCircle2 className="h-5 w-5 text-blue-600 mt-1 ml-auto" />
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}

                {selectedPackage && (
                  <Card className="mt-6">
                    <CardHeader>
                      <CardTitle className="text-lg">Payment Option</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <label className={`flex items-center justify-between p-4 rounded-lg border cursor-pointer ${paymentType === 'full' ? 'border-blue-500 bg-blue-50' : ''}`}>
                        <div className="flex items-center gap-3">
                          <input
                            type="radio"
                            name="paymentType"
                            checked={paymentType === 'full'}
                            onChange={() => setPaymentType('full')}
                            className="w-4 h-4"
                          />
                          <div>
                            <p className="font-medium">Full Payment</p>
                            <p className="text-sm text-gray-500">Pay the full amount now</p>
                          </div>
                        </div>
                        <span className="font-bold">${selectedPkg?.price}</span>
                      </label>
                      <label className={`flex items-center justify-between p-4 rounded-lg border cursor-pointer ${paymentType === 'deposit' ? 'border-blue-500 bg-blue-50' : ''}`}>
                        <div className="flex items-center gap-3">
                          <input
                            type="radio"
                            name="paymentType"
                            checked={paymentType === 'deposit'}
                            onChange={() => setPaymentType('deposit')}
                            className="w-4 h-4"
                          />
                          <div>
                            <p className="font-medium">50% Deposit</p>
                            <p className="text-sm text-gray-500">Pay remaining at appointment</p>
                          </div>
                        </div>
                        <span className="font-bold">${selectedPkg ? selectedPkg.price * 0.5 : 0}</span>
                      </label>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Reservation Form */}
              <Card>
                <CardHeader>
                  <CardTitle>Your Information</CardTitle>
                  <CardDescription>Enter your details to reserve your spot</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <Label htmlFor="name">Full Name</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={e => setFormData({...formData, name: e.target.value})}
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={e => setFormData({...formData, email: e.target.value})}
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="phone">Phone</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={e => setFormData({...formData, phone: e.target.value})}
                        required
                      />
                    </div>

                    <Separator />

                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex justify-between mb-2">
                        <span>Package:</span>
                        <span className="font-medium">{selectedPkg?.name || 'Not selected'}</span>
                      </div>
                      <div className="flex justify-between mb-2">
                        <span>Payment Type:</span>
                        <span className="font-medium">{paymentType === 'deposit' ? '50% Deposit' : 'Full Payment'}</span>
                      </div>
                      <Separator className="my-2" />
                      <div className="flex justify-between text-lg font-bold">
                        <span>Amount Due:</span>
                        <span>${amount}</span>
                      </div>
                    </div>

                    <Alert>
                      <Shield className="h-4 w-4" />
                      <AlertDescription>
                        Your payment information is secured with industry-standard encryption.
                      </AlertDescription>
                    </Alert>

                    <Button 
                      type="submit" 
                      className="w-full" 
                      size="lg"
                      disabled={submitting || !selectedPackage}
                    >
                      {submitting ? 'Processing...' : `Reserve Spot - $${amount}`}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

// ============== RESERVE SUCCESS/CANCEL PAGES ==============

const ReserveSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [reservation, setReservation] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const reservationId = searchParams.get('reservation_id');
    
    if (sessionId && reservationId) {
      axios.post(`${API}/reservations/verify-payment?session_id=${sessionId}&reservation_id=${reservationId}`)
        .then(res => setReservation(res.data.reservation))
        .catch(err => console.error(err))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [searchParams]);

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <p>Verifying payment...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <section className="py-20 min-h-screen bg-gray-50">
        <div className="max-w-2xl mx-auto px-4">
          <Card>
            <CardContent className="pt-8 text-center">
              <div className="w-20 h-20 rounded-full bg-green-100 text-green-600 flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="h-10 w-10" />
              </div>
              <h2 className="text-2xl font-bold mb-4">Payment Successful!</h2>
              <p className="text-gray-600 mb-6">
                Thank you for your payment. Your tax preparation spot has been reserved.
              </p>
              
              {reservation && (
                <div className="bg-gray-50 rounded-lg p-6 text-left mb-6">
                  <h3 className="font-semibold mb-4">Reservation Details:</h3>
                  <div className="space-y-2 text-sm">
                    <p><strong>Service:</strong> {reservation.package_name}</p>
                    <p><strong>Amount Paid:</strong> ${reservation.deposit_amount}</p>
                    <p><strong>Confirmation #:</strong> {reservation.id.slice(0, 8).toUpperCase()}</p>
                  </div>
                </div>
              )}

              <Alert className="text-left mb-6">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  We will contact you within 24 hours to schedule your tax preparation appointment.
                </AlertDescription>
              </Alert>

              <div className="flex gap-4 justify-center">
                <Button onClick={() => navigate('/')}>Return Home</Button>
                <Button variant="outline" onClick={() => navigate('/book')}>Book Appointment</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </Layout>
  );
};

const ReserveCancelPage = () => {
  const navigate = useNavigate();

  return (
    <Layout>
      <section className="py-20 min-h-screen bg-gray-50">
        <div className="max-w-2xl mx-auto px-4">
          <Card>
            <CardContent className="pt-8 text-center">
              <div className="w-20 h-20 rounded-full bg-yellow-100 text-yellow-600 flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="h-10 w-10" />
              </div>
              <h2 className="text-2xl font-bold mb-4">Payment Cancelled</h2>
              <p className="text-gray-600 mb-6">
                Your payment was cancelled. No charges have been made to your card.
              </p>
              <div className="flex gap-4 justify-center">
                <Button onClick={() => navigate('/reserve')}>Try Again</Button>
                <Button variant="outline" onClick={() => navigate('/')}>Return Home</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </Layout>
  );
};

// ============== CONTACT PAGE ==============

const ContactPage = () => {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success('Message sent successfully!');
    } catch (err) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <section className="py-20 bg-gray-50 min-h-screen">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Contact Us</h1>
            <p className="text-xl text-gray-600">We're here to help with your tax questions</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="text-2xl font-bold mb-6">Get in Touch</h2>
              <p className="text-gray-600 mb-8">
                Have questions about our tax preparation services? We're happy to help. Reach out by phone, email, or use the contact form.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0">
                    <Mail className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Email</h3>
                    <a href={`mailto:${BUSINESS.email}`} className="text-blue-600 hover:underline">
                      {BUSINESS.email}
                    </a>
                    <p className="text-sm text-gray-500 mt-1">We respond within 24 hours</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0">
                    <Phone className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Phone</h3>
                    <a href={`tel:${BUSINESS.phone.replace(/\s/g, '')}`} className="text-blue-600 hover:underline">
                      {BUSINESS.phoneFormatted}
                    </a>
                    <p className="text-sm text-gray-500 mt-1">Mon-Fri 9am-6pm, Sat 10am-2pm EST</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0">
                    <Clock className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Business Hours</h3>
                    <div className="text-sm text-gray-600 space-y-1">
                      <p>Monday - Friday: 9:00 AM - 6:00 PM</p>
                      <p>Saturday: 10:00 AM - 2:00 PM</p>
                      <p>Sunday: Closed</p>
                    </div>
                  </div>
                </div>
              </div>

              <Alert className="mt-8">
                <Shield className="h-4 w-4" />
                <AlertDescription>
                  <strong>Security Note:</strong> Tax documents are handled securely through a separate, encrypted process. Please do not send sensitive documents via this contact form.
                </AlertDescription>
              </Alert>
            </div>

            {/* Contact Form */}
            <Card>
              <CardHeader>
                <CardTitle>Send Us a Message</CardTitle>
                <CardDescription>Fill out the form below and we'll get back to you soon.</CardDescription>
              </CardHeader>
              <CardContent>
                {submitted ? (
                  <div className="text-center py-8">
                    <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Message Sent!</h3>
                    <p className="text-gray-600 mb-4">Thank you for reaching out. We'll get back to you within 24 hours.</p>
                    <Button onClick={() => {
                      setSubmitted(false);
                      setFormData({ name: '', email: '', phone: '', message: '' });
                    }}>
                      Send Another Message
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <Label htmlFor="name">Full Name</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={e => setFormData({...formData, name: e.target.value})}
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={e => setFormData({...formData, email: e.target.value})}
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="phone">Phone (Optional)</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={e => setFormData({...formData, phone: e.target.value})}
                      />
                    </div>

                    <div>
                      <Label htmlFor="message">Message</Label>
                      <Textarea
                        id="message"
                        rows={5}
                        value={formData.message}
                        onChange={e => setFormData({...formData, message: e.target.value})}
                        required
                        placeholder="How can we help you?"
                      />
                    </div>

                    <Button type="submit" className="w-full" disabled={submitting}>
                      {submitting ? 'Sending...' : 'Send Message'}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </Layout>
  );
};

// ============== PRIVACY & TERMS PAGES ==============

const PrivacyPage = () => (
  <Layout>
    <section className="py-20 bg-gray-50 min-h-screen">
      <div className="max-w-4xl mx-auto px-4">
        <h1 className="text-4xl font-bold mb-8">Privacy Policy</h1>
        <Card>
          <CardContent className="prose prose-gray max-w-none p-8">
            <p className="text-gray-600 mb-4">Last updated: {format(new Date(), 'MMMM d, yyyy')}</p>
            
            <h2 className="text-2xl font-semibold mt-6 mb-4">Information We Collect</h2>
            <p>We collect information you provide directly, including your name, email address, phone number, and information necessary for tax preparation services.</p>
            
            <h2 className="text-2xl font-semibold mt-6 mb-4">How We Use Your Information</h2>
            <p>We use your information to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Provide tax preparation services</li>
              <li>Communicate with you about your appointments</li>
              <li>Process payments</li>
              <li>Send important service updates</li>
            </ul>
            
            <h2 className="text-2xl font-semibold mt-6 mb-4">Data Security</h2>
            <p>We implement industry-standard security measures to protect your personal and financial information. Tax documents are handled through secure, encrypted processes.</p>
            
            <h2 className="text-2xl font-semibold mt-6 mb-4">Contact Us</h2>
            <p>For privacy-related questions, contact us at {BUSINESS.email}.</p>
          </CardContent>
        </Card>
      </div>
    </section>
  </Layout>
);

const TermsPage = () => (
  <Layout>
    <section className="py-20 bg-gray-50 min-h-screen">
      <div className="max-w-4xl mx-auto px-4">
        <h1 className="text-4xl font-bold mb-8">Terms of Service</h1>
        <Card>
          <CardContent className="prose prose-gray max-w-none p-8">
            <p className="text-gray-600 mb-4">Last updated: {format(new Date(), 'MMMM d, yyyy')}</p>
            
            <h2 className="text-2xl font-semibold mt-6 mb-4">Services</h2>
            <p>{BUSINESS.name} provides professional tax preparation services for individuals and small businesses.</p>
            
            <h2 className="text-2xl font-semibold mt-6 mb-4">Pricing</h2>
            <p>Prices are listed on our website. Final pricing may vary based on the complexity of your tax situation. You will be informed of any price changes before we file your return.</p>
            
            <h2 className="text-2xl font-semibold mt-6 mb-4">Appointments</h2>
            <p>Appointments can be scheduled through our online booking system. Please arrive on time with all necessary documents.</p>
            
            <h2 className="text-2xl font-semibold mt-6 mb-4">Cancellation Policy</h2>
            <p>We appreciate at least 24 hours notice for cancellations. Deposits are refundable if cancelled more than 48 hours in advance.</p>
            
            <h2 className="text-2xl font-semibold mt-6 mb-4">Limitation of Liability</h2>
            <p>While we strive for accuracy, the accuracy of your tax return depends on the information you provide. We are not liable for penalties resulting from incorrect information provided by clients.</p>
            
            <h2 className="text-2xl font-semibold mt-6 mb-4">Contact</h2>
            <p>Questions about these terms? Contact us at {BUSINESS.email}.</p>
          </CardContent>
        </Card>
      </div>
    </section>
  </Layout>
);

// ============== ADMIN PAGES ==============

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/admin/login`, formData);
      localStorage.setItem('adminToken', response.data.token);
      localStorage.setItem('adminEmail', response.data.admin.email);
      toast.success('Login successful!');
      navigate('/admin');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Building2 className="h-8 w-8 text-blue-600" />
            <span className="font-bold text-xl">JAJE Admin</span>
          </div>
          <CardTitle>Admin Login</CardTitle>
          <CardDescription>Enter your credentials to access the dashboard</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={e => setFormData({...formData, email: e.target.value})}
                required
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={e => setFormData({...formData, password: e.target.value})}
                required
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? 'Logging in...' : 'Login'}
            </Button>
          </form>
        </CardContent>
        <CardFooter className="justify-center">
          <Link to="/" className="text-sm text-gray-500 hover:text-gray-700">
            ← Back to website
          </Link>
        </CardFooter>
      </Card>
      <Toaster position="top-right" richColors />
    </div>
  );
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [apptQuery, setApptQuery] = useState('');
  const [apptStatusFilter, setApptStatusFilter] = useState('all');
  const [showCreateAppt, setShowCreateAppt] = useState(false);
  const [packages, setPackages] = useState([]);

// Manual Create Appointment form state
const [createApptForm, setCreateApptForm] = useState({
  customer_name: '',
  customer_email: '',
  customer_phone: '',
  package_id: '',
  date: '',   // YYYY-MM-DD
  time: '',   // HH:MM (24h)
  notes: ''
});

const [creatingAppt, setCreatingAppt] = useState(false);


  const token = localStorage.getItem('adminToken');

  const authHeaders = {
    headers: { Authorization: `Bearer ${token}` }
  };

  const fetchData = useCallback(async () => {
    if (!token) {
      navigate('/admin/login');
      return;
    }

    try {
      const [statsRes, apptsRes, resRes, msgsRes, pkgsRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard`, authHeaders),
        axios.get(`${API}/admin/appointments`, authHeaders),
        axios.get(`${API}/admin/reservations`, authHeaders),
        axios.get(`${API}/admin/messages`, authHeaders),
        axios.get(`${API}/packages`) // public endpoint
      ]);

      setStats(statsRes.data);
      setAppointments(apptsRes.data.appointments);
      setReservations(resRes.data.reservations);
      setMessages(msgsRes.data.messages);
      setPackages(pkgsRes.data.packages || pkgsRes.data || []);

    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/admin/login');
      }
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminEmail');
    navigate('/admin/login');
  };

  const updateAppointmentStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/appointments/${id}?status=${status}`, {}, authHeaders);
      toast.success('Appointment updated');
      fetchData();
    } catch (err) {
      toast.error('Failed to update appointment');
    }
  };

  const updateReservationStatus = async (id, payment_status) => {
    try {
      await axios.put(`${API}/admin/reservations/${id}?payment_status=${payment_status}`, {}, authHeaders);
      toast.success('Reservation updated');
      fetchData();
    } catch (err) {
      toast.error('Failed to update reservation');
    }
  };

  const exportCSV = async (type) => {
    try {
      const response = await axios.get(`${API}/admin/export/${type}`, authHeaders);
      const blob = new Blob([response.data.csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.data.filename;
      a.click();
      toast.success('Export downloaded');
    } catch (err) {
      toast.error('Export failed');
    }
  };
  const handleCreateAppointment = async () => {
  try {
    setCreatingAppt(true);

    // Basic validation
    if (!createApptForm.customer_name || !createApptForm.customer_email || !createApptForm.customer_phone) {
      toast.error("Please fill name, email, and phone.");
      return;
    }
    if (!createApptForm.package_id) {
      toast.error("Please select a package.");
      return;
    }
    if (!createApptForm.date || !createApptForm.time) {
      toast.error("Please select date and time.");
      return;
    }

    // Build ISO datetime from date + time (local)
    const start_time = new Date(`${createApptForm.date}T${createApptForm.time}:00`);

    const payload = {
      customer_name: createApptForm.customer_name.trim(),
      customer_email: createApptForm.customer_email.trim(),
      customer_phone: createApptForm.customer_phone.trim(),
      package_id: createApptForm.package_id,
      start_time: start_time.toISOString(),
      notes: createApptForm.notes || ""
    };

    await axios.post(`${API}/admin/appointments/create`, payload, authHeaders);

    toast.success("Appointment created. Emails sent.");
    setShowCreateAppt(false);
    setCreateApptForm({
      customer_name: '',
      customer_email: '',
      customer_phone: '',
      package_id: '',
      date: '',
      time: '',
      notes: ''
    });

    fetchData();
  } catch (err) {
    console.error(err);
    toast.error(err.response?.data?.detail || "Failed to create appointment");
  } finally {
    setCreatingAppt(false);
  }
};

const filteredAppointments = appointments.filter((appt) => {
  const q = apptQuery.trim().toLowerCase();

  const matchesQuery =
    !q ||
    (appt.customer_name || "").toLowerCase().includes(q) ||
    (appt.customer_email || "").toLowerCase().includes(q) ||
    (appt.customer_phone || "").toLowerCase().includes(q) ||
    (appt.package_name || "").toLowerCase().includes(q);

  const matchesStatus =
    apptStatusFilter === "all" || appt.status === apptStatusFilter;

  return matchesQuery && matchesStatus;
});

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <p>Loading dashboard...</p>
      </div>
    );
  }

  const statusBadge = (status) => {
    const colors = {
      confirmed: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      unpaid: 'bg-yellow-100 text-yellow-800',
      paid: 'bg-green-100 text-green-800',
      refunded: 'bg-gray-100 text-gray-800',
      failed: 'bg-red-100 text-red-800'
    };
 
    return <Badge className={colors[status] || 'bg-gray-100'}>{status}</Badge>;
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Admin Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <Building2 className="h-8 w-8 text-blue-600" />
            <div>
              <h1 className="text-xl font-bold">JAJE Admin Dashboard</h1>
              <p className="text-sm text-gray-500">{localStorage.getItem('adminEmail')}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/">
              <Button variant="outline" size="sm">View Site</Button>
            </Link>
            <Button variant="outline" size="sm" onClick={handleLogout}>
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Appointments</p>
                    <p className="text-3xl font-bold">{stats.appointments.total}</p>
                  </div>
                  <Calendar className="h-10 w-10 text-blue-500" />
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  {stats.appointments.confirmed} confirmed

                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Reservations</p>
                    <p className="text-3xl font-bold">{stats.reservations.total}</p>
                  </div>
                  <CreditCard className="h-10 w-10 text-green-500" />
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  {stats.reservations.paid} paid
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Revenue</p>
                    <p className="text-3xl font-bold">${stats.revenue.total}</p>
                  </div>
                  <DollarSign className="h-10 w-10 text-emerald-500" />
                </div>
                <p className="text-sm text-gray-500 mt-2">From paid reservations</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Unread Messages</p>
                    <p className="text-3xl font-bold">{stats.messages.unread}</p>
                  </div>
                  <Mail className="h-10 w-10 text-orange-500" />
                </div>
                <p className="text-sm text-gray-500 mt-2">Needs attention</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="appointments">Appointments</TabsTrigger>
            <TabsTrigger value="reservations">Reservations</TabsTrigger>
            <TabsTrigger value="messages">Messages</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Recent Appointments */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>Recent Appointments</CardTitle>
                  <Button variant="outline" size="sm" onClick={() => setActiveTab('appointments')}>
                    View All
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {appointments.slice(0, 5).map(appt => (
                      <div key={appt.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium">{appt.customer_name}</p>
                          <p className="text-sm text-gray-500">{appt.package_name}</p>
                          <p className="text-xs text-gray-400">
                            {format(parseISO(appt.start_time), 'MMM d, yyyy h:mm a')}
                          </p>
                        </div>
                        {statusBadge(appt.status)}
                      </div>
                    ))}
                    {appointments.length === 0 && (
                      <p className="text-gray-500 text-center py-4">No appointments yet</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Recent Reservations */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>Recent Reservations</CardTitle>
                  <Button variant="outline" size="sm" onClick={() => setActiveTab('reservations')}>
                    View All
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {reservations.slice(0, 5).map(res => (
                      <div key={res.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium">{res.customer_name}</p>
                          <p className="text-sm text-gray-500">{res.package_name} - ${res.deposit_amount}</p>
                          <p className="text-xs text-gray-400">
                            {format(parseISO(res.created_at), 'MMM d, yyyy')}
                          </p>
                        </div>
                        {statusBadge(res.payment_status)}
                      </div>
                    ))}
                    {reservations.length === 0 && (
                      <p className="text-gray-500 text-center py-4">No reservations yet</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="appointments">
            <Card>
              <CardHeader className="flex flex-col gap-4">
  <div className="flex items-center justify-between">
    <CardTitle>All Appointments</CardTitle>

    <div className="flex gap-2">
      <Button size="sm" onClick={() => setShowCreateAppt(true)}>
        ➕ Create Appointment
      </Button>

      <Button variant="outline" size="sm" onClick={() => exportCSV('appointments')}>
        <Download className="h-4 w-4 mr-2" />
        Export CSV
      </Button>
    </div>
  </div>

  <div className="flex flex-col md:flex-row gap-3">
    <Input
      placeholder="Search name, email, phone..."
      value={apptQuery}
      onChange={(e) => setApptQuery(e.target.value)}
      className="md:w-1/2"
    />

    <Select value={apptStatusFilter} onValueChange={setApptStatusFilter}>
      <SelectTrigger className="md:w-48">
        <SelectValue placeholder="Filter by status" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All</SelectItem>
        <SelectItem value="confirmed">Confirmed</SelectItem>
        <SelectItem value="completed">Completed</SelectItem>
        <SelectItem value="cancelled">Cancelled</SelectItem>
      </SelectContent>
    </Select>
  </div>
</CardHeader>


              <CardContent>
{/* Filters */}

  <div className="flex-1">
    <Input
      placeholder="Search name, email, phone, package..."
      value={apptQuery}
      onChange={(e) => setApptQuery(e.target.value)}
    />
  </div>

  <div className="w-full md:w-56">
    <Select value={apptStatusFilter} onValueChange={setApptStatusFilter}>
      <SelectTrigger>
        <SelectValue placeholder="Filter by status" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All statuses</SelectItem>
        <SelectItem value="confirmed">Confirmed</SelectItem>
        <SelectItem value="completed">Completed</SelectItem>
        <SelectItem value="cancelled">Cancelled</SelectItem>
      </SelectContent>
    </Select>
  </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-2">Customer</th>
                        <th className="text-left py-3 px-2">Package</th>
                        <th className="text-left py-3 px-2">Date/Time</th>
                        <th className="text-left py-3 px-2">Status</th>
                        <th className="text-left py-3 px-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAppointments.map(appt => (

                        <tr key={appt.id} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-2">
                            <p className="font-medium">{appt.customer_name}</p>
                            <p className="text-sm text-gray-500">{appt.customer_email}</p>
                            <p className="text-sm text-gray-500">{appt.customer_phone}</p>
                          </td>
                          <td className="py-3 px-2">
                            <p>{appt.package_name}</p>
                            <p className="text-sm text-gray-500">${appt.package_price}</p>
                          </td>
                          <td className="py-3 px-2">
                            <p>{format(parseISO(appt.start_time), 'MMM d, yyyy')}</p>
                            <p className="text-sm text-gray-500">{format(parseISO(appt.start_time), 'h:mm a')}</p>
                          </td>
                          <td className="py-3 px-2">{statusBadge(appt.status)}</td>
                          <td className="py-3 px-2">
                            <Select
                              value={appt.status}
                              onValueChange={(value) => updateAppointmentStatus(appt.id, value)}
                            >
                              <SelectTrigger className="w-32">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="confirmed">Confirmed</SelectItem>
                                <SelectItem value="completed">Completed</SelectItem>
                                <SelectItem value="cancelled">Cancelled</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredAppointments.length === 0 && (

                    <p className="text-gray-500 text-center py-8">No appointments yet</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="reservations">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>All Reservations</CardTitle>
                <Button variant="outline" size="sm" onClick={() => exportCSV('reservations')}>
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-2">Customer</th>
                        <th className="text-left py-3 px-2">Package</th>
                        <th className="text-left py-3 px-2">Amount</th>
                        <th className="text-left py-3 px-2">Payment</th>
                        <th className="text-left py-3 px-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reservations.map(res => (
                        <tr key={res.id} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-2">
                            <p className="font-medium">{res.customer_name}</p>
                            <p className="text-sm text-gray-500">{res.customer_email}</p>
                            <p className="text-sm text-gray-500">{res.customer_phone}</p>
                          </td>
                          <td className="py-3 px-2">{res.package_name}</td>
                          <td className="py-3 px-2">
                            <p>${res.deposit_amount}</p>
                            <p className="text-sm text-gray-500">
                              {res.payment_type === 'deposit' ? '50% deposit' : 'Full payment'}
                            </p>
                          </td>
                          <td className="py-3 px-2">{statusBadge(res.payment_status)}</td>
                          <td className="py-3 px-2">
                            <Select
                              value={res.payment_status}
                              onValueChange={(value) => updateReservationStatus(res.id, value)}
                            >
                              <SelectTrigger className="w-32">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="unpaid">Unpaid</SelectItem>
                                <SelectItem value="paid">Paid</SelectItem>
                                <SelectItem value="refunded">Refunded</SelectItem>
                                <SelectItem value="failed">Failed</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {reservations.length === 0 && (
                    <p className="text-gray-500 text-center py-8">No reservations yet</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="messages">
            <Card>
              <CardHeader>
                <CardTitle>Contact Messages</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {messages.map(msg => (
                    <Card key={msg.id} className={msg.read ? 'opacity-60' : ''}>
                      <CardContent className="pt-4">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-medium">{msg.name}</p>
                            <p className="text-sm text-gray-500">{msg.email} • {msg.phone}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-gray-400">
                              {format(parseISO(msg.created_at), 'MMM d, yyyy h:mm a')}
                            </p>
                            {!msg.read && <Badge className="mt-1 bg-blue-100 text-blue-800">New</Badge>}
                          </div>
                        </div>
                        <p className="text-gray-700 whitespace-pre-wrap">{msg.message}</p>
                      </CardContent>
                    </Card>
                  ))}
                  {messages.length === 0 && (
                    <p className="text-gray-500 text-center py-8">No messages yet</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
      {showCreateAppt && (
  <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
    <div className="bg-white w-full max-w-md rounded-xl shadow-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold">Create Appointment</h2>
        <Button variant="outline" size="sm" onClick={() => setShowCreateAppt(false)}>
          Close
        </Button>
      </div>

      <p className="text-sm text-gray-600">
        This is the manual appointment popup. Next we will add the form fields and submit to backend.
      </p>
    </div>
  </div>
)}

      <Toaster position="top-right" richColors />
    <Dialog open={showCreateAppt} onOpenChange={setShowCreateAppt}>
  <DialogContent className="max-w-lg">
    <DialogHeader>
      <DialogTitle>Create Appointment</DialogTitle>
      <DialogDescription>
        Create a walk-in / phone / WhatsApp appointment and send confirmation + intake form.
      </DialogDescription>
    </DialogHeader>

    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label>Name</Label>
          <Input
            value={createApptForm.customer_name}
            onChange={(e) => setCreateApptForm({ ...createApptForm, customer_name: e.target.value })}
            placeholder="Client full name"
          />
        </div>

        <div>
          <Label>Phone</Label>
          <Input
            value={createApptForm.customer_phone}
            onChange={(e) => setCreateApptForm({ ...createApptForm, customer_phone: e.target.value })}
            placeholder="(317) 000-0000"
          />
        </div>
      </div>

      <div>
        <Label>Email</Label>
        <Input
          type="email"
          value={createApptForm.customer_email}
          onChange={(e) => setCreateApptForm({ ...createApptForm, customer_email: e.target.value })}
          placeholder="client@email.com"
        />
      </div>

      <div>
        <Label>Package</Label>
        <Select
          value={createApptForm.package_id}
          onValueChange={(value) => setCreateApptForm({ ...createApptForm, package_id: value })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select a package" />
          </SelectTrigger>
          <SelectContent>
            {(packages || []).map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.name} — ${Number(p.price).toFixed(2)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label>Date</Label>
          <Input
            type="date"
            value={createApptForm.date}
            onChange={(e) => setCreateApptForm({ ...createApptForm, date: e.target.value })}
          />
        </div>

        <div>
          <Label>Time</Label>
          <Input
            type="time"
            value={createApptForm.time}
            onChange={(e) => setCreateApptForm({ ...createApptForm, time: e.target.value })}
          />
        </div>
      </div>

      <div>
        <Label>Notes (optional)</Label>
        <Textarea
          value={createApptForm.notes}
          onChange={(e) => setCreateApptForm({ ...createApptForm, notes: e.target.value })}
          placeholder="Walk-in, phone call, WhatsApp, documents reminder..."
        />
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => setShowCreateAppt(false)} disabled={creatingAppt}>
          Cancel
        </Button>
        <Button onClick={handleCreateAppointment} disabled={creatingAppt}>
          {creatingAppt ? "Creating..." : "Create & Send Emails"}
        </Button>
      </div>
    </div>
  </DialogContent>
</Dialog>
</div>
  );
};


// ============== APP ROUTER ==============

function App() {

  return (

  <BrowserRouter>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/book" element={<BookAppointmentPage />} />
        <Route path="/reserve" element={<ReserveSpotPage />} />
        <Route path="/reserve/success" element={<ReserveSuccessPage />} />
        <Route path="/reserve/cancel" element={<ReserveCancelPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route path="/admin" element={<AdminDashboard />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
